import React from 'react';

/**
 * Componente de entrada para valores monetarios con formato.
 * Muestra el símbolo "$" como prefijo visual integrado al borde del input.
 * 
 * @param {string} label - El texto de la etiqueta que se muestra arriba del input.
 * @param {string} value - El valor formateado actual.
 * @param {function} onChange - El handler que procesa los cambios (comúnmente handleCurrencyChange).
 * @param {string} placeholder - Texto temporal de guía (Default: "0,00").
 * @param {string} focusColor - Clases Tailwind para cambiar el color del borde y foco (útil para variar según el riesgo).
 * @param {boolean} disabled - Define si el campo se encuentra deshabilitado.
 * @param {boolean} required - Define si el campo es obligatorio.
 */
export const CurrencyInput = ({ 
    label, 
    value, 
    onChange, 
    placeholder = "0,00", 
    focusColor = "focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-100", 
    disabled = false,
    required = false
}) => {
    return (
        <div className="w-full">
            {label && <label className="label-form text-slate-500 font-bold mb-1 block">{label}</label>}
            <div 
                className={`flex items-center border border-slate-200 rounded-xl overflow-hidden h-[46px] bg-white transition-all ${
                    disabled ? 'bg-slate-50/50 opacity-60 cursor-not-allowed' : focusColor
                }`}
            >
                {/* Prefijo con el símbolo de pesos */}
                <div className="bg-slate-50 px-3 h-full flex items-center border-r border-slate-100 font-bold text-slate-400 text-xs shrink-0 select-none">
                    $
                </div>
                <input 
                    type="text" 
                    value={value || ''} 
                    onChange={onChange} 
                    className="w-full px-3 outline-none font-mono font-bold text-slate-700 placeholder:text-slate-300 disabled:bg-transparent h-full text-left" 
                    placeholder={placeholder} 
                    disabled={disabled}
                    required={required}
                />
            </div>
        </div>
    );
};

/**
 * Componente de entrada para valores porcentuales con formato.
 * Muestra el símbolo "%" como sufijo visual integrado al borde del input.
 * 
 * @param {string} label - El texto de la etiqueta que se muestra arriba del input.
 * @param {string} name - Atributo 'name' para identificar la variable en el estado de formData.
 * @param {string|number} value - El valor numérico o formateado actual.
 * @param {function} onChange - El handler que procesa los cambios (comúnmente handlePercentChange).
 * @param {string} placeholder - Texto temporal de guía (Default: "0,00").
 * @param {string} focusColor - Clases Tailwind para cambiar el color del borde y foco.
 * @param {string} textColor - Color para distinguir el texto (Ej: 'text-blue-700' para comisiones).
 * @param {boolean} disabled - Define si el campo se encuentra deshabilitado.
 * @param {boolean} required - Define si el campo es obligatorio.
 */
export const PercentInput = ({ 
    label, 
    name, 
    value, 
    onChange, 
    placeholder = "0,00", 
    focusColor = "focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-100", 
    textColor = "text-slate-700",
    disabled = false,
    required = false
}) => {
    return (
        <div className="w-full">
            {label && <label className="label-form text-slate-500 font-bold mb-1 block">{label}</label>}
            <div 
                className={`flex items-center border border-slate-200 rounded-xl overflow-hidden h-[46px] bg-white transition-all ${
                    disabled ? 'bg-slate-50/50 opacity-60 cursor-not-allowed' : focusColor
                }`}
            >
                <input 
                    type="text" 
                    name={name}
                    value={value || ''} 
                    onChange={onChange} 
                    className={`w-full px-3 outline-none font-mono font-bold text-right placeholder:text-slate-300 disabled:bg-transparent h-full ${textColor}`}
                    placeholder={placeholder} 
                    disabled={disabled}
                    required={required}
                />
                {/* Sufijo con el símbolo de porcentaje */}
                <div className="bg-slate-50 px-3 h-full flex items-center border-l border-slate-100 font-bold text-slate-300 text-[10px] shrink-0 select-none">
                    %
                </div>
            </div>
        </div>
    );
};